import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { coursesAPI, videosAPI, paymentAPI, leadsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Loader2, Play, Lock, ChevronLeft, CheckCircle, Star, ShoppingCart, Clock, Users, Award, Mail } from 'lucide-react';
import { formatPrice, extractInstructor } from '../lib/utils';
import { toast } from 'sonner';
import { SEO } from '../components/SEO';

// Helper to generate instructor slug
const nameToSlug = (name) => name?.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') || '';

// Instructor-specific testimonials — 3 per instructor, 2 randomly selected on page load
const COURSE_TESTIMONIALS = {
  'tony pro': [
    {
      quote: "His approach to limited palette mixing made three years of self-study click in one session. I finally understand why the shadows need to stay transparent.",
      name: "C. Hartmann",
      location: "Seattle",
    },
    {
      quote: "The pacing is deliberate — he'll sit and look at the painting for a long time before touching it. That alone changed how I work.",
      name: "M. Okonkwo",
      location: "Lagos",
    },
    {
      quote: "The still life sessions are worth the price alone. He shows you exactly when to stop, which is something nobody else had taught me.",
      name: "A. Vieira",
      location: "Lisbon",
    },
  ],
  'glen orbik': [
    {
      quote: "Glen doesn't flatten the Reilly method into a formula. He shows you where it actually breaks down and how to think through it. That's rare.",
      name: "T. Brennan",
      location: "Dublin",
    },
    {
      quote: "The first three classes gave me a working vocabulary for value and structure that I still use every day in the studio.",
      name: "D. Larsson",
      location: "Stockholm",
    },
    {
      quote: "He talks about specific decisions — not rules, decisions. That distinction made the instruction land differently for me.",
      name: "F. Lemaire",
      location: "Lyon",
    },
  ],
  'jeremy lipking': [
    {
      quote: "Watching him work outdoors and manage changing light in real time — no book teaches that. The figure painting session is exceptional.",
      name: "R. Navarro",
      location: "Madrid",
    },
    {
      quote: "He talks through his uncertainty as he paints, which makes it honest. The section on edges in outdoor portraiture alone is worth the price.",
      name: "H. Müller",
      location: "Munich",
    },
    {
      quote: "The colour temperature work in the landscape sessions is the clearest explanation of warm and cool light I've encountered.",
      name: "S. Takahashi",
      location: "Kyoto",
    },
  ],
  'fred fixler': [
    {
      quote: "The Reilly method felt abstract until Fred showed it on a live model. The way he builds form from the inside out is something I hadn't seen taught clearly before.",
      name: "A. Vieira",
      location: "Lisbon",
    },
    {
      quote: "I've studied other instructors in this tradition. Fred is the one who explains the why behind each decision, not just the what.",
      name: "P. Santos",
      location: "São Paulo",
    },
    {
      quote: "His approach to the halftones is methodical without being mechanical. I've rewatched those sections more than anything else.",
      name: "G. Andersen",
      location: "Copenhagen",
    },
  ],
  'steve cefalo': [
    {
      quote: "Steve's figure work has a solidity that's hard to teach. He manages to explain the structural thinking without reducing it to a formula.",
      name: "K. Park",
      location: "Seoul",
    },
    {
      quote: "The sessions on drapery are worth it on their own. Clear, methodical, and he doesn't skip over the parts that are actually difficult.",
      name: "L. Bergström",
      location: "Gothenburg",
    },
    {
      quote: "I appreciated that he didn't try to be charismatic — just clear. The instruction is dense in the best way.",
      name: "N. Osei",
      location: "Accra",
    },
  ],
  default: [
    {
      quote: "The kind of instruction I couldn't find in art school — methodical, honest, no shortcuts or empty technique.",
      name: "J. Barnes",
      location: "Chicago",
    },
    {
      quote: "I've watched the lessons multiple times. The teaching holds up on review, which is rare for this format.",
      name: "M. Reyes",
      location: "Mexico City",
    },
    {
      quote: "Production quality is excellent but it's the depth of the instruction that keeps me coming back.",
      name: "E. Nkosi",
      location: "Johannesburg",
    },
  ],
};

export const CourseDetailPage = () => {
    const { courseId } = useParams();
    const { isAuthenticated } = useAuth();
    const [course, setCourse] = useState(null);
    const [hasAccess, setHasAccess] = useState(false);
    const [ownedPackage, setOwnedPackage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [purchasing, setPurchasing] = useState(null);
    const [error, setError] = useState('');
    const [leadEmail, setLeadEmail] = useState('');
    const [leadLoading, setLeadLoading] = useState(false);
    const [leadCaptured, setLeadCaptured] = useState(false);

    const handleLeadCapture = async (e) => {
        e.preventDefault();
        if (!leadEmail) return;
        setLeadLoading(true);
        try {
            await leadsAPI.capture(leadEmail);
            setLeadCaptured(true);
            setLeadEmail('');
        } catch {
            toast.error('Something went wrong. Please try again.');
        } finally {
            setLeadLoading(false);
        }
    };

    const handlePurchase = async (packageId) => {
        if (!isAuthenticated) {
            toast.error('Please sign in to purchase');
            return;
        }
        
        setPurchasing(packageId);
        try {
            const response = await paymentAPI.createCheckout(packageId);
            // GA4: track begin_checkout event
            if (typeof window.gtag === 'function') {
                window.gtag('event', 'begin_checkout', {
                    currency: 'USD',
                    items: [{ item_id: packageId, item_name: course?.title || packageId }],
                });
            }
            // Redirect to Stripe checkout
            window.location.href = response.data.url;
        } catch (err) {
            console.error('Purchase error:', err);
            const message = err.response?.data?.detail || 'Failed to start checkout';
            toast.error(message);
            setPurchasing(null);
        }
    };

    useEffect(() => {
        const fetchCourse = async () => {
            setLoading(true);
            try {
                const response = await coursesAPI.getById(courseId);
                setCourse(response.data);

                // Check access for the first lesson
                if (isAuthenticated && response.data.lessons?.length > 0) {
                    try {
                        const accessRes = await videosAPI.checkAccess(response.data.lessons[0].id);
                        const hasLesson1Access = accessRes.data.has_access;
                        setHasAccess(hasLesson1Access);
                        
                        // Check which package they own
                        if (hasLesson1Access && response.data.packages) {
                            // Check if they have access to lesson 4 (determines starter vs complete)
                            const lesson4 = response.data.lessons.find(l => l.lesson_number === 4);
                            if (lesson4) {
                                try {
                                    const access4 = await videosAPI.checkAccess(lesson4.id);
                                    if (access4.data.has_access) {
                                        setOwnedPackage('complete');
                                    } else {
                                        setOwnedPackage('starter');
                                    }
                                } catch {
                                    // If error checking lesson 4, assume starter
                                    setOwnedPackage('starter');
                                }
                            } else {
                                // No lesson 4, assume complete
                                setOwnedPackage('complete');
                            }
                        }
                    } catch {
                        setHasAccess(false);
                    }
                }
            } catch (err) {
                setError('Failed to load course');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchCourse();
    }, [courseId, isAuthenticated]);

    // Derive instructor early so useMemo can reference it unconditionally (Rules of Hooks)
    const instructor = course?.instructor || (course ? extractInstructor(course.title) : null);

    // Pick 2 testimonials for this instructor, randomly selected once on page load.
    // Must be called before any early returns to satisfy Rules of Hooks.
    const testimonials = useMemo(() => {
        if (!instructor) return [];
        const key = instructor.toLowerCase();
        const pool = COURSE_TESTIMONIALS[key] || COURSE_TESTIMONIALS.default;
        const shuffled = [...pool].sort(() => Math.random() - 0.5);
        return shuffled.slice(0, 2);
    }, [instructor]);

    if (loading) {
        return (
            <div className="min-h-[50vh] flex items-center justify-center" data-testid="course-detail-loading">
                <Loader2 className="h-8 w-8 animate-spin text-accent" />
            </div>
        );
    }

    if (error || !course) {
        return (
            <div className="min-h-[50vh] flex items-center justify-center" data-testid="course-detail-error">
                <div className="text-center">
                    <p className="text-destructive mb-4">{error || 'Course not found'}</p>
                    <Link to="/courses" className="text-accent hover:underline">View all courses</Link>
                </div>
            </div>
        );
    }
    const firstLesson = course.lessons?.[0];
    const hasPackages = course.packages && course.packages.length > 1;
    
    // Sort packages: starter, complete, upgrade
    const sortedPackages = hasPackages 
        ? [...course.packages].sort((a, b) => {
            const order = { starter: 1, complete: 2, upgrade: 3 };
            return (order[a.package_type] || 99) - (order[b.package_type] || 99);
        })
        : null;

    const starterPkg = sortedPackages?.find(p => p.package_type === 'starter');
    const completePkg = sortedPackages?.find(p => p.package_type === 'complete');
    const upgradePkg = sortedPackages?.find(p => p.package_type === 'upgrade');

    // Calculate course duration estimate (assume 2 hours per lesson)
    const estimatedHours = (course.lessons?.length || 1) * 2;
    const durationISO = `PT${estimatedHours}H`;

    // Generate SEO description
    const seoDescription = course.description 
        ? course.description.substring(0, 160) + '...'
        : `Learn ${course.title} with ${instructor}. ${course.lessons?.length || 0} professional lessons with lifetime access.`;

    // Course Schema.org structured data
    const courseSchema = {
        "@context": "https://schema.org",
        "@type": "Course",
        "name": course.title,
        "description": course.description || seoDescription,
        "provider": {
            "@type": "Organization",
            "name": "Zarolla Online Art Academy",
            "sameAs": "https://zarolla.com"
        },
        "offers": {
            "@type": "Offer",
            "price": completePkg?.cost || course.cost || 0,
            "priceCurrency": "USD",
            "availability": "https://schema.org/InStock",
            "validFrom": "2024-01-01"
        },
        "hasCourseInstance": {
            "@type": "CourseInstance",
            "courseMode": "online",
            "courseWorkload": durationISO
        },
        "numberOfCredits": course.lessons?.length || 1,
        "educationalLevel": "Intermediate",
        "inLanguage": "en",
        "isAccessibleForFree": false,
        ...(instructor && {
            "instructor": {
                "@type": "Person",
                "name": instructor,
                "url": `https://zarolla.com/instructor/${nameToSlug(instructor)}`
            }
        }),
        "audience": {
            "@type": "Audience",
            "audienceType": "Artists and Art Students"
        },
        "teaches": [
            "Oil Painting Techniques",
            "Portrait Painting",
            "Figure Drawing",
            "Classical Art Methods"
        ].slice(0, 3),
        "image": course.thumbnail || "https://zarolla.com/og-image.jpg",
        "url": `https://zarolla.com/courses/${courseId}`
    };

    // VideoObject schema for the course content
    const videoSchema = course.lessons?.length > 0 ? {
        "@context": "https://schema.org",
        "@type": "VideoObject",
        "name": course.title,
        "description": course.description || seoDescription,
        "thumbnailUrl": course.thumbnail,
        "uploadDate": "2024-01-01",
        "duration": durationISO,
        "contentUrl": `https://zarolla.com/courses/${courseId}`,
        "embedUrl": `https://zarolla.com/courses/${courseId}`,
        "publisher": {
            "@type": "Organization",
            "name": "Zarolla Online Art Academy",
            "logo": {
                "@type": "ImageObject",
                "url": "https://customer-assets.emergentagent.com/job_video-rebuild-2/artifacts/96keensf_Zarolla_logo_final_allwhite_nosub.png"
            }
        }
    } : null;

    // Generate keywords based on course content
    const seoKeywords = [
        'online art classes',
        instructor ? `${instructor} course` : null,
        course.title.toLowerCase().includes('alla prima') ? 'alla prima painting' : null,
        course.title.toLowerCase().includes('reilly') ? 'Frank Reilly method' : null,
        course.title.toLowerCase().includes('drawing') ? 'drawing course' : null,
        course.title.toLowerCase().includes('portrait') ? 'portrait painting' : null,
    ].filter(Boolean);

    return (
        <>
            {/* Dynamic SEO with Course Schema */}
            <SEO
                title={`${course.title} | ${instructor}`}
                description={seoDescription}
                image={course.thumbnail}
                url={`/courses/${courseId}`}
                keywords={seoKeywords}
                course={{
                    title: course.title,
                    description: course.description,
                    instructor: instructor,
                    thumbnail: course.thumbnail,
                    cost: completePkg?.cost || course.cost,
                    duration: `${estimatedHours} hours`,
                }}
                structuredData={courseSchema}
            />

            <div className="section-gap" data-testid="course-detail-page">
            <div className="container-main">
                {/* Back link */}
                <Link 
                    to="/courses"
                    className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8"
                    data-testid="back-to-courses"
                >
                    <ChevronLeft className="h-4 w-4" />
                    Back to Courses
                </Link>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                    {/* Main Content */}
                    <div className="lg:col-span-2">
                        {/* Course Header */}
                        <div className="mb-8">
                            <span className="text-xs font-semibold uppercase tracking-widest text-accent">
                                Course • {course.lessons?.length || 0} Lessons
                            </span>
                            <h1 className="font-serif text-3xl md:text-4xl font-semibold text-primary mt-2" data-testid="course-title">
                                {course.title}
                            </h1>
                            {instructor && (
                                <Link 
                                    to={`/instructor/${nameToSlug(instructor)}`}
                                    className="text-lg text-muted-foreground mt-2 hover:text-accent transition-colors inline-block"
                                    data-testid="course-instructor"
                                >
                                    with {instructor}
                                </Link>
                            )}
                            
                            {/* Course Quick Stats */}
                            <div className="flex flex-wrap gap-4 mt-4">
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <Clock className="h-4 w-4" />
                                    <span>{estimatedHours}+ hours of content</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <Play className="h-4 w-4" />
                                    <span>{course.lessons?.length || 0} video lessons</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <Award className="h-4 w-4" />
                                    <span>Lifetime access</span>
                                </div>
                            </div>
                        </div>

                        {/* What You'll Learn */}
                        <div className="mb-8 p-6 bg-secondary/30 rounded-lg">
                            <h2 className="font-serif text-xl font-medium mb-4 flex items-center gap-2">
                                <CheckCircle className="h-5 w-5 text-accent" />
                                What You'll Learn
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div className="flex items-start gap-2">
                                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                                    <span className="text-sm text-muted-foreground">Professional techniques from a master artist</span>
                                </div>
                                <div className="flex items-start gap-2">
                                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                                    <span className="text-sm text-muted-foreground">Step-by-step demonstrations you can follow</span>
                                </div>
                                <div className="flex items-start gap-2">
                                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                                    <span className="text-sm text-muted-foreground">Materials and methods used by professionals</span>
                                </div>
                                <div className="flex items-start gap-2">
                                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                                    <span className="text-sm text-muted-foreground">Insights from years of professional experience</span>
                                </div>
                            </div>
                        </div>

                        {/* Course Description */}
                        {course.description && (
                            <div className="mb-8" data-testid="course-description">
                                <h2 className="font-serif text-xl font-medium mb-4">About This Course</h2>
                                <div className="text-muted-foreground leading-relaxed space-y-4">
                                    {course.description.split('\n\n').map((paragraph, index) => (
                                        <p key={index}>{paragraph}</p>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Lessons List */}
                        <div data-testid="course-lessons">
                            <h2 className="font-serif text-xl font-medium mb-4">
                                Lessons ({course.lessons?.length || 0})
                            </h2>
                            <div className="space-y-2">
                                {course.lessons?.map((lesson, index) => {
                                    const lessonNum = lesson.lesson_number || index + 1;
                                    const isInStarter = starterPkg?.includes_lessons?.includes(lessonNum);
                                    // For courses without packages, if user has access, they can access all lessons
                                    // For tiered packages: complete = all lessons, starter = only lessons in starter package
                                    const canAccess = hasAccess && (!hasPackages || ownedPackage === 'complete' || (ownedPackage === 'starter' && isInStarter));
                                    
                                    // Make the entire card clickable if user has access
                                    const CardWrapper = canAccess ? Link : 'div';
                                    const cardProps = canAccess ? { to: `/video/${lesson.id}` } : {};
                                    
                                    return (
                                        <CardWrapper key={lesson.id} {...cardProps} className="block">
                                            <Card 
                                                className={`p-4 transition-colors ${canAccess ? 'hover:border-accent cursor-pointer hover:bg-accent/5' : 'opacity-75'}`}
                                                data-testid={`course-lesson-${lesson.id}`}
                                            >
                                                <div className="flex items-center gap-4">
                                                    <div className={`w-10 h-10 rounded flex items-center justify-center flex-shrink-0 ${
                                                        canAccess ? 'bg-accent/10' : 'bg-secondary'
                                                    }`}>
                                                        {canAccess ? (
                                                            <Play className="h-4 w-4 text-accent" />
                                                        ) : (
                                                            <span className="text-sm font-medium text-muted-foreground">
                                                                {String(lessonNum).padStart(2, '0')}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <h3 className="font-medium truncate">
                                                            {lesson.title || `Session ${lessonNum}`}
                                                        </h3>
                                                        {!canAccess && hasPackages && (
                                                            <span className="text-xs text-muted-foreground">
                                                                {isInStarter ? 'Included in First 3 Classes' : 'Included in complete course'}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {canAccess ? (
                                                        <div className="flex items-center gap-2 text-accent">
                                                            <span className="text-sm font-medium">Watch</span>
                                                            <Play className="h-4 w-4" />
                                                        </div>
                                                    ) : (
                                                        <Lock className="h-4 w-4 text-muted-foreground" />
                                                    )}
                                                </div>
                                            </Card>
                                        </CardWrapper>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Lead capture — shown to non-owners browsing this course */}
                        {!hasAccess && (
                            <div
                                className="mt-10 p-6 border border-border rounded-sm bg-secondary/20"
                                data-testid="course-lead-capture"
                            >
                                <span className="text-xs font-semibold uppercase tracking-widest text-accent">
                                    Free resource
                                </span>
                                <h3 className="font-serif text-lg font-medium text-primary mt-2">
                                    Free: 10 Drawing Principles from a Reilly Master
                                </h3>
                                <p className="text-sm text-muted-foreground mt-2 max-w-md">
                                    Foundational principles on value structure, edge control, and constructing a drawing that holds together — taught by Fred Fixler, one of Frank Reilly's most respected students.
                                </p>
                                {leadCaptured ? (
                                    <p className="mt-4 text-sm font-medium text-green-600" data-testid="lead-capture-success">
                                        Check your inbox — your guide is on its way.
                                    </p>
                                ) : (
                                    <form onSubmit={handleLeadCapture} className="mt-4 flex flex-col sm:flex-row gap-3 max-w-md">
                                        <div className="relative flex-1">
                                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                            <input
                                                type="email"
                                                required
                                                placeholder="Your email address"
                                                value={leadEmail}
                                                onChange={(e) => setLeadEmail(e.target.value)}
                                                className="w-full pl-10 pr-4 py-2.5 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
                                                data-testid="course-lead-email"
                                            />
                                        </div>
                                        <Button
                                            type="submit"
                                            disabled={leadLoading}
                                            className="bg-accent text-accent-foreground hover:bg-accent/90 flex-shrink-0"
                                            data-testid="course-lead-submit"
                                        >
                                            {leadLoading ? 'Sending...' : 'Get Free Guide'}
                                        </Button>
                                    </form>
                                )}
                                <p className="text-xs text-muted-foreground/60 mt-3">No spam. Unsubscribe anytime.</p>
                            </div>
                        )}
                    </div>

                    {/* Sidebar */}
                    <div className="lg:col-span-1">
                        <div className="sticky top-24 space-y-4">
                            {/* Thumbnail */}
                            <Card className="overflow-hidden">
                                <div className="aspect-video bg-muted">
                                    <img 
                                        src={course.thumbnail || 'https://images.unsplash.com/photo-1752649937419-45f8210078d2?crop=entropy&cs=srgb&fm=jpg&q=85&w=400'}
                                        alt={course.title}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            </Card>

                            {/* Pricing Options */}
                            {hasPackages ? (
                                <div className="space-y-3" data-testid="course-packages">
                                    {/* Starter Package */}
                                    {starterPkg && (
                                        <Card className={`p-4 ${ownedPackage === 'starter' ? 'border-green-500 bg-green-50' : ''}`}>
                                            <div className="flex justify-between items-start mb-2">
                                                <div>
                                                    <h3 className="font-medium">First 3 Classes</h3>
                                                    <p className="text-xs text-muted-foreground">
                                                        Try the course
                                                    </p>
                                                </div>
                                                <span className="text-xl font-semibold">{formatPrice(starterPkg.cost)}</span>
                                            </div>
                                            {ownedPackage === 'starter' ? (
                                                <div className="flex items-center gap-2 text-green-600 text-sm">
                                                    <CheckCircle className="h-4 w-4" />
                                                    You own this
                                                </div>
                                            ) : !isAuthenticated ? (
                                                <Button asChild size="sm" variant="outline" className="w-full mt-2">
                                                    <Link to="/login">Sign In</Link>
                                                </Button>
                                            ) : (
                                                <Button 
                                                    size="sm" 
                                                    variant="outline" 
                                                    className="w-full mt-2"
                                                    onClick={() => handlePurchase(starterPkg.id)}
                                                    disabled={purchasing === starterPkg.id}
                                                    data-testid="buy-starter-btn"
                                                >
                                                    {purchasing === starterPkg.id ? (
                                                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                                    ) : (
                                                        <ShoppingCart className="h-4 w-4 mr-2" />
                                                    )}
                                                    Buy Now
                                                </Button>
                                            )}
                                        </Card>
                                    )}

                                    {/* Complete Package */}
                                    {completePkg && (() => {
                                        // Calculate savings vs buying lessons individually
                                        const lessonPrice = 50; // Each lesson costs $50
                                        const totalIndividual = (course.lessons?.length || 10) * lessonPrice;
                                        const savings = totalIndividual - completePkg.cost;
                                        const savingsPercent = Math.round((savings / totalIndividual) * 100);
                                        
                                        return (
                                        <Card className={`p-4 border-2 ${ownedPackage === 'complete' ? 'border-green-500 bg-green-50' : 'border-accent'}`}>
                                            <div className="flex justify-between items-start mb-2">
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <h3 className="font-medium">Complete Course</h3>
                                                        <Badge variant="secondary" className="bg-accent/10 text-accent text-xs">
                                                            <Star className="h-3 w-3 mr-1" />
                                                            Best Value
                                                        </Badge>
                                                    </div>
                                                    <p className="text-xs text-muted-foreground">
                                                        All {course.lessons?.length} classes
                                                    </p>
                                                    {savings > 0 && (
                                                        <p className="text-xs text-green-600 font-medium mt-1">
                                                            Save {formatPrice(savings)} ({savingsPercent}% off vs individual)
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="text-right">
                                                    <span className="text-xl font-semibold">{formatPrice(completePkg.cost)}</span>
                                                    {savings > 0 && (
                                                        <p className="text-xs text-muted-foreground line-through">
                                                            {formatPrice(totalIndividual)}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            {ownedPackage === 'complete' ? (
                                                <div className="flex items-center gap-2 text-green-600 text-sm">
                                                    <CheckCircle className="h-4 w-4" />
                                                    You own this
                                                </div>
                                            ) : !isAuthenticated ? (
                                                <Button asChild className="w-full mt-2 bg-accent text-accent-foreground hover:bg-accent/90">
                                                    <Link to="/login">Sign In to Purchase</Link>
                                                </Button>
                                            ) : (
                                                <Button 
                                                    className="w-full mt-2 bg-accent text-accent-foreground hover:bg-accent/90"
                                                    onClick={() => handlePurchase(completePkg.id)}
                                                    disabled={purchasing === completePkg.id}
                                                    data-testid="buy-complete-btn"
                                                >
                                                    {purchasing === completePkg.id ? (
                                                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                                    ) : (
                                                        <ShoppingCart className="h-4 w-4 mr-2" />
                                                    )}
                                                    Buy Complete Course
                                                </Button>
                                            )}
                                        </Card>
                                        );
                                    })()}

                                    {/* Upgrade Package */}
                                    {upgradePkg && ownedPackage === 'starter' && (
                                        <Card className="p-4 border-dashed border-2 border-accent/50">
                                            <div className="flex justify-between items-start mb-2">
                                                <div>
                                                    <h3 className="font-medium">Upgrade to Complete</h3>
                                                    <p className="text-xs text-muted-foreground">
                                                        Classes 4-10
                                                    </p>
                                                </div>
                                                <span className="text-xl font-semibold">{formatPrice(upgradePkg.cost)}</span>
                                            </div>
                                            <Button 
                                                variant="outline"
                                                className="w-full mt-2 border-accent text-accent hover:bg-accent/10"
                                                onClick={() => handlePurchase(upgradePkg.id)}
                                                disabled={purchasing === upgradePkg.id}
                                                data-testid="buy-upgrade-btn"
                                            >
                                                {purchasing === upgradePkg.id ? (
                                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                                ) : (
                                                    <ShoppingCart className="h-4 w-4 mr-2" />
                                                )}
                                                Upgrade Now
                                            </Button>
                                        </Card>
                                    )}
                                </div>
                            ) : (
                                /* Single Price */
                                <Card className="p-6" data-testid="course-sidebar">
                                    {course.cost > 0 && (
                                        <div className="text-center mb-6">
                                            <p className="text-3xl font-semibold">{formatPrice(course.cost)}</p>
                                            <p className="text-sm text-muted-foreground mt-1">
                                                Full course access
                                            </p>
                                        </div>
                                    )}

                                    {hasAccess ? (
                                        <div className="space-y-4">
                                            <div className="flex items-center justify-center gap-2 text-green-600 py-2">
                                                <CheckCircle className="h-5 w-5" />
                                                <span className="font-medium">You own this course</span>
                                            </div>
                                            {firstLesson && (
                                                <Button asChild className="w-full bg-accent text-accent-foreground hover:bg-accent/90">
                                                    <Link to={`/video/${firstLesson.id}`} data-testid="start-course-btn">
                                                        <Play className="h-4 w-4 mr-2" />
                                                        Start Learning
                                                    </Link>
                                                </Button>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="space-y-4">
                                            {!isAuthenticated ? (
                                                <Button asChild className="w-full bg-accent text-accent-foreground hover:bg-accent/90">
                                                    <Link to="/login" data-testid="sign-in-for-course">
                                                        Sign In to Access
                                                    </Link>
                                                </Button>
                                            ) : (
                                                <p className="text-sm text-muted-foreground text-center">
                                                    Contact us for purchase options
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </Card>
                            )}

                            {/* Course Info */}
                            <Card className="p-4">
                                <div className="space-y-3 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Total Lessons</span>
                                        <span className="font-medium">{course.lessons?.length || 0}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Access</span>
                                        <span className="font-medium">Lifetime</span>
                                    </div>
                                    {instructor && (
                                        <div className="flex justify-between">
                                            <span className="text-muted-foreground">Instructor</span>
                                            <span className="font-medium">{instructor}</span>
                                        </div>
                                    )}
                                </div>
                            </Card>

                            {/* Start Learning Button (if has access) */}
                            {hasAccess && firstLesson && hasPackages && (
                                <Button asChild className="w-full bg-accent text-accent-foreground hover:bg-accent/90">
                                    <Link to={`/video/${firstLesson.id}`} data-testid="start-course-btn">
                                        <Play className="h-4 w-4 mr-2" />
                                        Start Learning
                                    </Link>
                                </Button>
                            )}

                            {/* Social proof — testimonials */}
                            {testimonials.length > 0 && (
                                <div className="pt-3 border-t border-border" data-testid="sidebar-testimonials">
                                    <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-4">
                                        What students say
                                    </p>
                                    <div className="space-y-5">
                                        {testimonials.map((t, i) => (
                                            <div key={i} data-testid={`testimonial-${i}`}>
                                                <p className="text-sm text-muted-foreground leading-relaxed">
                                                    &ldquo;{t.quote}&rdquo;
                                                </p>
                                                <p className="text-xs text-muted-foreground/55 mt-2 font-medium tracking-wide">
                                                    &mdash; {t.name}, {t.location}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
        </>
    );
};

export default CourseDetailPage;
